import { isEmpty, isFunction, isNil } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import UnsavedChangesAlert from "../components/common/UnsavedChangesAlert";
import RffFormChanges from "../components/finalForm/RffFormChanges";
import { useUnderwritingHook } from "../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../redux/actions/loanDrilldownActions";
import { loanPipelineClear } from "../redux/actions/loanPipelineActions";
import { loanFetchedDataLoanTermsSet } from "../redux/reducers/loanFetchedDataReducer";

const LoanDrilldownChangesForm = ({
  data,
  showFooter = false,
  formSpy = false,
  children,
  onSuccessFn,
  sx,
  borrowerId,
  // For a host that renders its OWN footer (the quick-edit modal): the form
  // takes an id its Save can submit by, and reports dirty + in-flight back so
  // that button can enable itself and spin.
  formId,
  onHasChangesChange,
  onLoadingChange,
}) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const dispatch = useDispatch();

  const [loanDrilldownInitialValues, setLoanDrilldownInitialValues] =
    useState(null);

  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const [formKey, setFormKey] = useState(0); // Key to force form re-initialization

  const initialValues = useMemo(() => {
    return data || loanDrilldown;
  }, [data, loanDrilldown]);
  // const { loading, updateLoanData, updateBorrower } = useUnderwritingHook();
  // const handleUpdateFieldNew = useCallback((changedData) => {
  //   if (borrowerId) {
  //     updateBorrower({
  //       borrowerId: borrowerId,
  //       loanId: loanDrilldown?._id,
  //       data: changedData,
  //       onSuccessFn: (response) => {
  //         dispatch(loanDrilldownSet(response));
  //         dispatch(loanPipelineClear());
  //         if (isFunction(onSuccessFn)) {
  //           onSuccessFn(response);
  //         }
  //       },
  //     });
  //     return;
  //   }

  //   const ex = {
  //     loanProductType: "Example",
  //     salesperson: null,
  //   };
  //   updateLoanData({
  //     loanId: loanDrilldown?._id,
  //     data: changedData,
  //     onSuccessFn: (response) => {
  //       dispatch(loanDrilldownSet(response));
  //       if (!isEmpty(loanTerms)) {
  //         dispatch(loanFetchedDataLoanTermsSet(null));
  //       }
  //       dispatch(loanPipelineClear());
  //       // let the "loading" spinner turn off
  //     },
  //     onFailFn: () => {
  //       dispatch(loanDrilldownSet(loanDrilldown));
  //     },
  //   });
  // }, []);
  const { loading, updateLoanData, updateBorrower, unsetLoanDataFields } =
    useUnderwritingHook();

  const UNSETTABLE_FIELDS = new Set([
    "salesperson",
    "processor",
    "preUnderwriter",
    "qc_analyst",
    "underwriter",
    "loanCloser",
    "referralPartner",
    "referralSource",
    "investorCounterparty",
    "fundingEntity",
    "subStatus",
    "exitStrategy",
    "sync_loan_id",
    "interestCalcType",
    "originator",
  ]);

  const handleUpdateFieldNew = useCallback((changedData) => {
    if (borrowerId) {
      updateBorrower({
        borrowerId: borrowerId,
        loanId: loanDrilldown?._id,
        data: changedData,
        onSuccessFn: (response) => {
          dispatch(loanDrilldownSet(response));
          dispatch(loanPipelineClear());
          if (isFunction(onSuccessFn)) {
            onSuccessFn(response);
          }
        },
      });
      return;
    }

    // Separate null/undefined unsettable fields from regular updates
    const fieldsToUnset = [];
    const updateData = {};

    Object.entries(changedData).forEach(([key, value]) => {
      if (UNSETTABLE_FIELDS.has(key) && isNil(value)) {
        fieldsToUnset.push(key);
      } else {
        updateData[key] = value;
      }
    });

    const onSuccessHandler = (response) => {
      dispatch(loanDrilldownSet(response));
      if (!isEmpty(loanTerms)) {
        dispatch(loanFetchedDataLoanTermsSet(null));
      }
      dispatch(loanPipelineClear());
      // The BORROWER branch above has always told its host it succeeded; this
      // one never did, so a modal wired to close on save (every
      // `UpdateLoanRootDataFieldsModal`) just sat there after a successful
      // write and had to be dismissed by hand.
      if (isFunction(onSuccessFn)) {
        onSuccessFn(response);
      }
    };

    const onFailHandler = () => {
      console.log("resetting to ", loanDrilldown);
      // dispatch(loanDrilldownSet(loanDrilldown));
    };

    // Unset null unsettable fields

    if (isEmpty(updateData) && !isEmpty(fieldsToUnset)) {
      unsetLoanDataFields({
        loanId: loanDrilldown?._id,
        data: { fields: fieldsToUnset },
        onSuccessFn: onSuccessHandler,
        onFailFn: onFailHandler,
      });
      return;
    }

    // Update remaining fields normally
    if (!isEmpty(updateData)) {
      updateLoanData({
        loanId: loanDrilldown?._id,
        data: updateData,
        onSuccessFn: (res) => {
          if (!isEmpty(fieldsToUnset)) {
            console.log("Second", { fieldsToUnset });
            unsetLoanDataFields({
              loanId: loanDrilldown?._id,
              data: { fields: fieldsToUnset },
              onSuccessFn: onSuccessHandler,
              onFailFn: onFailHandler,
            });
            return;
          }
          onSuccessHandler(res);
        },
        onFailFn: onFailHandler,
      });
    }
  }, []);
  useEffect(() => {
    if (isFunction(onLoadingChange)) onLoadingChange(loading);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    if (isNil(loanDrilldownInitialValues)) {
      setTimeout(() => {
        setLoanDrilldownInitialValues(loanDrilldown);
        setFormKey((prev) => prev + 1); // Force form re-initialization
      }, 100);
    } else {
      setFormKey((prev) => prev + 1);
    }
  }, [loanDrilldown]);
  return (
    <RffFormChanges
      key={formKey}
      id={formId}
      onHasChangesChange={onHasChangesChange}
      initialValues={initialValues}
      suppressFooter={!showFooter}
      sx={sx}
      loading={loading}
      formSpy={formSpy}
      onSubmit={handleUpdateFieldNew}
      hasChangesContent={
        showFooter ? null : (
          <UnsavedChangesAlert
            loading={loading}
            onCancel={() => {
              setLoanDrilldownInitialValues(loanDrilldownInitialValues);
              setFormKey((prev) => prev + 1);
            }}
          />
        )
      }
    >
      {children}
    </RffFormChanges>
  );
};

export default LoanDrilldownChangesForm;
